import Ajv from "ajv";
import {
  formatPlanningHourFormat,
  formatPlanningMileageFormat,
  formatPlanningStartDateFormat,
  hasPlanningHourFormat,
  hasPlanningMileageFormat,
  hasPlanningStartDateFormat,
  normalizeExcelHeader,
} from "@qivia/ui/src/format";
import { DriverPlannings } from "./driverPlanningsApi";
import { getStartTimeAndEndTimeFromExcel } from "./lib";
import { driverPlanningHeaderColumns } from "./driverPlanningsHeaders";

export type DriverPlanningInputLine = {
  driverIdNumber: string;
  startDate: string;
  startTime: string;
  endTime: string;
  expectedMileage: string;
  driverCity?: string;
  clientName?: string;
  clientFirstName?: string;
  clientCity?: string;
  activityType?: string;
};

type DriverPlanningInputLineColumn = keyof DriverPlanningInputLine;

const columnByNormalizedHeader = driverPlanningHeaderColumns.reduce(
  (acc, column) => {
    column.acceptedHeaders.forEach((header) => {
      acc[normalizeExcelHeader(header)] = column.field;
    });
    return acc;
  },
  {} as Record<string, DriverPlanningInputLineColumn>,
);

export const normalizeDriverPlanningColumns = (
  rawLine: Record<string, unknown>,
): DriverPlanningInputLine => {
  const normalizedLine: Partial<DriverPlanningInputLine> = {};

  Object.entries(rawLine).forEach(([header, value]) => {
    const column = columnByNormalizedHeader[normalizeExcelHeader(header)];
    if (column && value !== undefined && value !== null) {
      normalizedLine[column] = String(value);
    }
  });

  return normalizedLine as DriverPlanningInputLine;
};

const schema = {
  type: "array",
  items: {
    type: "object",
    properties: {
      driverIdNumber: {
        type: "string",
      },
      startDate: {
        type: "string",
        format: "planningStartDate",
      },
      startTime: {
        type: "string",
        format: "planningHour",
      },
      endTime: {
        type: "string",
        format: "planningHour",
      },
      expectedMileage: {
        type: "string",
        format: "planningMileage",
      },
      driverCity: {
        type: "string",
      },
      clientName: {
        type: "string",
      },
      clientFirstName: {
        type: "string",
      },
      clientCity: {
        type: "string",
      },
      activityType: {
        type: "string",
      },
    },
    required: [
      "driverIdNumber",
      "startDate",
      "startTime",
      "endTime",
      "expectedMileage",
    ],
  },
};

const ajv = new Ajv({ formats: { date: "date" } });
ajv.addFormat("planningStartDate", {
  validate: (value: string) => hasPlanningStartDateFormat(value),
});
ajv.addFormat("planningHour", {
  validate: (value: string) => hasPlanningHourFormat(value),
});
ajv.addFormat("planningMileage", {
  validate: (value: string) => hasPlanningMileageFormat(value),
});

export const validate = ajv.compile(schema);

export const parseLine = (line: DriverPlanningInputLine): DriverPlannings => {
  const formattedStartDate = formatPlanningStartDateFormat(line.startDate);
  if (!formattedStartDate) {
    throw new Error("Invalid planning start date format");
  }
  const [day, month, year] = formattedStartDate;
  const formattedStartTime = formatPlanningHourFormat(line.startTime);
  const formattedEndTime = formatPlanningHourFormat(line.endTime);
  if (!formattedStartTime || !formattedEndTime) {
    throw new Error("Invalid planning hour format");
  }
  const formattedExpectedMileage = formatPlanningMileageFormat(
    line.expectedMileage,
  );
  if (!formattedExpectedMileage) {
    throw new Error("Invalid planning mileage format");
  }

  const { startTime, endTime } = getStartTimeAndEndTimeFromExcel(
    year,
    month,
    day,
    {
      hours: parseInt(formattedStartTime.slice(0, 2)),
      minutes: parseInt(formattedStartTime.slice(2, 4)),
    },
    {
      hours: parseInt(formattedEndTime.slice(0, 2)),
      minutes: parseInt(formattedEndTime.slice(2, 4)),
    },
  );
  const hasKmClient = Boolean(
    line.activityType &&
      line.activityType.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() ===
        "kmsclient",
  );

  return {
    driverIdNumber: line.driverIdNumber,
    expectedMileage: parseFloat(formattedExpectedMileage),
    startTime,
    endTime,
    driverCity: line.driverCity ?? null,
    clientName:
      line.clientName && line.clientFirstName
        ? `${line.clientFirstName} ${line.clientName}`
        : null,
    clientCity: line.clientCity ?? null,
    hasKmClient,
    activityType: line.activityType ?? null,
  };
};
